import { Alert, App as AntApp, Button, Descriptions, Drawer, Form, Grid, Input, Popconfirm, Space, Spin, Table, Tag, Typography } from "antd";
import { DownloadOutlined, LockOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { excuseKeys, excuseStatusColor, useExcuseRequest, type ExcuseRequestDetails, type ExcuseRequestLesson } from "../app/excuses.ts";
import { saveBlob } from "../app/files.ts";
import { formatSchoolDateTime } from "../app/time.ts";

const DATE_FORMAT = "DD.MM.YYYY";
/** Mirrors the API's limit on the decision note and the rejection reason. */
const NOTE_MAX_LENGTH = 500;

interface Props {
  id: string | null;
  onClose: () => void;
}

interface DecisionForm {
  note?: string;
}

/**
 * One excuse request with the absences it covers; staff holding attendance.excuse approve it (the covered absences become
 * excused, with the parent's reason and the approver in the audit trail) or reject it with a reason (attendance unchanged).
 */
export function ExcuseRequestDrawer({ id, onClose }: Props) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const request = useExcuseRequest(id);
  const data = request.data;

  return (
    <Drawer
      title={data ? t("attendance.excuses.drawerTitle", { name: data.studentName }) : t("attendance.excuses.title")}
      open={id !== null}
      onClose={onClose}
      size={screens.md ? 560 : "100%"}
      destroyOnHidden
    >
      {request.isPending && <Spin description={t("app.loading")} />}
      {request.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void request.refetch()}>{t("app.retry")}</Button>} />}
      {data && <RequestBody request={data} />}
    </Drawer>
  );
}

function RequestBody({ request }: { request: ExcuseRequestDetails }) {
  const { t } = useTranslation();
  const date = (value: string) => dayjs(value).format(DATE_FORMAT);
  const period = request.from === request.to ? date(request.from) : t("attendance.excuses.period", { from: date(request.from), to: date(request.to) });
  const pending = request.status === "Pending";
  const absencesTitle = pending ? "attendance.excuses.absencesTitle" : "attendance.excuses.excusedTitle";

  return (
    <Space orientation="vertical" size="large" style={{ width: "100%" }}>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label={t("attendance.excuses.student")}>{request.studentName}</Descriptions.Item>
        {request.className && <Descriptions.Item label={t("attendance.excuses.className")}>{request.className}</Descriptions.Item>}
        <Descriptions.Item label={t("attendance.excuses.columns.period")}>{period}</Descriptions.Item>
        <Descriptions.Item label={t("attendance.excuses.submittedBy")}>{request.submittedByName}</Descriptions.Item>
        <Descriptions.Item label={t("attendance.excuses.submittedAt")}>{formatSchoolDateTime(request.submittedAt)}</Descriptions.Item>
        <Descriptions.Item label={t("attendance.excuses.reason")}>
          <Typography.Text style={{ whiteSpace: "pre-wrap" }}>{request.reason}</Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label={t("attendance.excuses.document")}>
          {request.documentFileName ? <DocumentButton request={request} /> : <Typography.Text type="secondary">{t("attendance.excuses.noDocument")}</Typography.Text>}
        </Descriptions.Item>
        <Descriptions.Item label={t("attendance.excuses.columns.status")}>
          <Tag color={excuseStatusColor[request.status]}>{t(`attendance.excuses.statuses.${request.status}`)}</Tag>
        </Descriptions.Item>
        {request.decidedByName && request.decidedAt && (
          <Descriptions.Item label={t("attendance.excuses.decision")}>
            {t("attendance.excuses.decidedBy", { name: request.decidedByName, time: formatSchoolDateTime(request.decidedAt) })}
          </Descriptions.Item>
        )}
        {request.decisionNote && <Descriptions.Item label={t("attendance.excuses.decisionNote")}>{request.decisionNote}</Descriptions.Item>}
      </Descriptions>
      {request.status !== "Rejected" && (
        <div>
          <Typography.Title level={5}>{t(absencesTitle, { count: request.absences.length })}</Typography.Title>
          {request.absences.length === 0 ? (
            <Typography.Text type="secondary">{t("attendance.excuses.noAbsences")}</Typography.Text>
          ) : (
            <Table<ExcuseRequestLesson>
              size="small"
              bordered
              pagination={false}
              showHeader={false}
              rowKey="lessonId"
              dataSource={request.absences}
              columns={[
                {
                  key: "lesson",
                  render: (_: unknown, a: ExcuseRequestLesson) => (
                    <Space wrap>
                      <span>{t("attendance.excuses.absence", { date: date(a.date), period: Number(a.period), subject: a.subjectName })}</span>
                      {a.isLocked && <LockOutlined aria-label={t("attendance.locked")} />}
                    </Space>
                  ),
                },
                { key: "status", width: 170, render: (_: unknown, a: ExcuseRequestLesson) => <Tag>{t(`attendance.statuses.${a.status}`)}</Tag> },
              ]}
            />
          )}
        </div>
      )}
      {pending && <DecisionPanel request={request} />}
    </Space>
  );
}

function DocumentButton({ request }: { request: ExcuseRequestDetails }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const download = useMutation({
    mutationFn: async () => {
      const { data, error, response } = await api.GET("/attendance/excuse-requests/{id}/document", { params: { path: { id: request.id } }, parseAs: "blob" });
      if (!data) throw new ApiError(response.status, error);
      saveBlob(data, request.documentFileName ?? "hujjat");
    },
    onError: () => void message.error(t("app.error")),
  });
  return (
    <Button type="link" icon={<DownloadOutlined />} loading={download.isPending} onClick={() => download.mutate()} style={{ paddingInline: 0 }}>
      {t("attendance.excuses.downloadNamed", { name: request.documentFileName ?? "" })}
    </Button>
  );
}

function DecisionPanel({ request }: { request: ExcuseRequestDetails }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<DecisionForm>();
  const locked = request.absences.some((a) => a.isLocked);

  const onDecided = async (details: ExcuseRequestDetails, text: string) => {
    queryClient.setQueryData(excuseKeys.detail(details.id), details);
    await queryClient.invalidateQueries({ queryKey: excuseKeys.all });
    void message.success(text);
  };
  const reportError = (err: unknown) => void message.error((err instanceof ApiError ? err.messages(t("app.error")) : [t("app.error")]).join(" "));

  const approve = useMutation({
    mutationFn: async () => {
      const note = form.getFieldValue("note") as string | undefined;
      const { data, error, response } = await api.POST("/attendance/excuse-requests/{id}/approve", {
        params: { path: { id: request.id } },
        body: { note: note?.trim() ? note.trim() : null },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: (details) => onDecided(details, t("attendance.excuses.approved")),
    onError: reportError,
  });

  const reject = useMutation({
    mutationFn: async (reason: string) => {
      const { data, error, response } = await api.POST("/attendance/excuse-requests/{id}/reject", { params: { path: { id: request.id } }, body: { reason } });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: (details) => onDecided(details, t("attendance.excuses.rejected")),
    onError: reportError,
  });

  const onReject = async () => {
    const note = ((form.getFieldValue("note") as string | undefined) ?? "").trim();
    if (!note) {
      form.setFields([{ name: "note", errors: [t("attendance.excuses.rejectReasonRequired")] }]);
      return;
    }
    reject.mutate(note);
  };

  return (
    <Form<DecisionForm> form={form} layout="vertical" requiredMark={false}>
      {locked && <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("attendance.excuses.lockedHint")} />}
      <Form.Item name="note" label={t("attendance.excuses.approveNote")} extra={t("attendance.excuses.rejectReason")}>
        <Input.TextArea rows={3} maxLength={NOTE_MAX_LENGTH} showCount />
      </Form.Item>
      <Space wrap>
        <Popconfirm
          title={t("attendance.excuses.approveConfirm", { count: request.absences.length })}
          okText={t("attendance.excuses.approve")}
          cancelText={t("attendance.excuses.cancel")}
          onConfirm={() => approve.mutate()}
          disabled={locked}
        >
          <Button type="primary" loading={approve.isPending} disabled={locked || reject.isPending}>
            {t("attendance.excuses.approve")}
          </Button>
        </Popconfirm>
        <Button danger loading={reject.isPending} disabled={approve.isPending} onClick={() => void onReject()}>
          {t("attendance.excuses.reject")}
        </Button>
      </Space>
    </Form>
  );
}
